<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useApiClient } from "@/api/client";
import router from "@/router/router";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const route = useRoute();

// Every steering set is priced by its own rule model, so each set has its
// own versions and rules. Keyed by the backend's PredefinedModelNames.
const steeringSets = [
	{
		ruleModelName: "pqa-cpm-netto-standaard",
		titleKey: "pqa.rules.sets.netto",
	},
	{
		ruleModelName: "pqa-cpm-bruto-standaard",
		titleKey: "pqa.rules.sets.bruto",
	},
];

const defaultRuleModelName = "pqa-cpm-netto-standaard";

const ruleModelUuids = ref<Record<string, string>>({});

const resolveRuleModelUuid = async (
	ruleModelName: string
): Promise<string | null> => {
	if (ruleModelUuids.value[ruleModelName] === undefined) {
		const response = await useApiClient("ruleEngine").showModelByName(
			ruleModelName
		);

		if (!response.isOk()) return null;

		ruleModelUuids.value[ruleModelName] = response.value.uuid;
	}

	return ruleModelUuids.value[ruleModelName];
};

const openVersions = async (ruleModelName: string) => {
	const ruleModelUuid = await resolveRuleModelUuid(ruleModelName);

	if (ruleModelUuid === null) return;

	await router.replace({
		name: "pqa.rules.versions",
		params: { model_uuid: ruleModelUuid },
	});
};

// Falls back to Netto while the model uuids are still resolving, so the
// switch never renders with nothing selected.
const selectedRuleModelName = computed<string>({
	get: () =>
		Object.keys(ruleModelUuids.value).find(
			(name) => ruleModelUuids.value[name] === route.params.model_uuid
		) ?? defaultRuleModelName,
	set: (ruleModelName) => openVersions(ruleModelName),
});

// Landing on the bare /pqa/rules path: open the default set's versions list
// (the child routes render the versions page and the builder). This
// component stays mounted while the child routes change, so watch the route
// instead of relying on mount hooks - otherwise navigating back to
// /pqa/rules (e.g. via the Rules tab) leaves an empty router-view.
const redirectToVersions = async () => {
	if (route.name !== "pqa.rules") return;

	await openVersions(defaultRuleModelName);
};

watch(() => route.name, redirectToVersions, { immediate: true });

// Resolve every set up front so the toggle can tell which set the current
// model_uuid belongs to, including after a reload on a versions URL.
steeringSets.forEach((set) => resolveRuleModelUuid(set.ruleModelName));
</script>

<template>
	<v-btn-toggle
		v-if="route.name === 'pqa.rules.versions'"
		v-model="selectedRuleModelName"
		mandatory
		density="compact"
		variant="outlined"
		color="primary"
		rounded="lg"
		class="mb-4"
	>
		<v-btn
			v-for="set in steeringSets"
			:key="set.ruleModelName"
			:value="set.ruleModelName"
			size="small"
		>
			{{ t(set.titleKey) }}
		</v-btn>
	</v-btn-toggle>

	<!-- Keyed on the model so switching sets reloads the versions list. -->
	<router-view :key="String(route.params.model_uuid ?? '')" />
</template>

<style scoped lang="scss"></style>
